import { ReactElement, useEffect, useState } from "react";
import { Breadcrumb, Button, Layout, Space, Table, Avatar, Tooltip, message } from "antd";
import { EditOutlined, DeleteOutlined, ShareAltOutlined, ScheduleOutlined, PartitionOutlined, PlusOutlined, CheckOutlined, DashboardOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Clipboard from "react-clipboard.js";
import { BreadCrumbWrapper, RightButton } from "../../components/GlobalStyles";
import { DeleteClubModal } from "./DeleteClubModal";
import { EditClubDrawer } from "./EditClubDrawer";
import { CreateClubDrawer } from "./CreateClubDrawer";
import { getAuthState, getClubState } from "../../redux/selectors";
import { FETCH_ALL_CLUBS_REQUEST, FETCH_ALL_USERS_REQUEST } from "../../redux/constants";
import { countries, clubStatus } from "../../constants";

const { Content } = Layout;


export function ClubManagement(): ReactElement {
  const dispatch = useDispatch();
  const history = useHistory();
  const { clubs } = useSelector(getClubState);
  const { currentUser } = useSelector(getAuthState);
  const [createClubDrawerVisible, setCreateClubDrawerVisible] = useState(false);
  const [editClubDrawerVisible, setEditClubDrawerVisible] = useState(false);
  const [deleteClubModalVisible, setDeleteClubModalVisible] = useState(false);
  const [selectedClub, setSelectedClub] = useState<any>();
  const [baseUrl, setBaseUrl] = useState<string>("");
  const { t } = useTranslation();

  const isAdmin = currentUser && currentUser.role === "admin";

  useEffect(() => {
    const path = `${window.location.protocol}//${window.location.host}`;
    setBaseUrl(path);
    dispatch({ type: FETCH_ALL_CLUBS_REQUEST, payload: { currentUser } });
    dispatch({ type: FETCH_ALL_USERS_REQUEST });
  }, []);

  const onChange = (pagination: any, filters: any, sorter: any, extra: any) => {
    // eslint-disable-next-line no-console
    console.log("params", pagination, filters, sorter, extra);
  };

  const goClubDetail = (record: any) => {
    history.push(`clubs/${record.key}`);
  };

  const onEdit = (record: any) => {
    setEditClubDrawerVisible(true);
    setSelectedClub(record);
  };

  const onDelete = (record: any) => {
    setDeleteClubModalVisible(true);
    setSelectedClub(record);
  };

  const goToBooking = ({key}: any) => {
    history.push(`/clubs/${key}/bookings`);
  };

  const onSuccessCopyClubLink = () => {
    message.success("Club link copied!");
  };

  const goDashboard = ({ key }: any) => {
    history.push(`/clubs/${key}/dashboard`);
  }
 
  const columns: any = [
    {
      title: "",
      key: "logo",
      render: ({photo}: any) => <Avatar src={photo} size={30} />,
    },
    {
      title: t("club.club"),
      dataIndex: "name",
      key: "name",
    },
    {
      // title: t("club.branch_count"),
      title: "Branches",
      key: "countOfBranches",
      render: ({countOfBranches}: any) => <span>{countOfBranches}</span>,
    },
    {
      title: "Status",
      key: "status",
      render: ({status}: any) => {
        const statusObject = clubStatus.find((s) => s.status === status);
        return <span>{statusObject?.label}</span>
      },
    },
    {
      title: t("club.website"),
      key: "website",
      render: ({ website }: any ) => <a href={website} target="_blank" rel="noreferrer">Visit Website</a>
    },
    {
      title: t("club.contact_email"),
      dataIndex: "contactEmail",
      key: "contactEmail"
    },
    {
      title: t("club.contact_number"),
      dataIndex: "contactPhone",
      key: "contactPhone"
    },
    {
      title: "Country",
      key: "country",
      render: ({ country }:any) => {
        const countryObject = countries.find((c) => c.code === country);
        return <span>{countryObject?.country}</span>
      }
    },
    {
      title: "Payment",
      key: "payment",
      render: ({ payOverCount }: any) =>(
        <span>{payOverCount? "Pay over count" : "Online"}</span>
      )
    },
    {
      title: t("common.action"),
      key: "action",
      align: "center",
      render: (record: any) => (
        <Space size="small">
          {currentUser?.role === "admin" && 
            <Tooltip placement="top" title="Dashboard">
              <Button type="primary" shape="circle" onClick={() => goDashboard(record)}><DashboardOutlined /></Button>
            </Tooltip>
          }
          <Tooltip placement="top" title="Edit club">
            <Button type="primary" shape="circle" onClick={() => onEdit(record)}><EditOutlined /></Button>
          </Tooltip>
          <Tooltip placement="top" title="See branches">
            <Button type="primary" shape="circle" onClick={() => goClubDetail(record)}>
              <PartitionOutlined />
            </Button>
          </Tooltip>
          <Tooltip placement="top" title="See bookings">
            <Button type="primary" shape="circle" onClick={() => goToBooking(record)}><ScheduleOutlined /></Button>
          </Tooltip>
          <Tooltip placement="top" title="Share">
            <Clipboard 
              data-clipboard-text={`${baseUrl}/share/${record.key}`} 
              onSuccess={onSuccessCopyClubLink} 
              className="ant-btn ant-btn-primary ant-btn-circle"
            >
              <ShareAltOutlined />
            </Clipboard>
          </Tooltip>
          <Tooltip placement="top" title="Delete Club">
            <Button danger type="primary" shape="circle" onClick={() => onDelete(record)}><DeleteOutlined /></Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Content>
      <BreadCrumbWrapper>
        <Breadcrumb style={{ fontSize: 18 }}>
          <Breadcrumb.Item>{t("club.clubs")}</Breadcrumb.Item>
        </Breadcrumb>
        {isAdmin && (
          <RightButton
            type="primary"
            onClick={() => setCreateClubDrawerVisible(true)}
            icon={ <PlusOutlined /> }
          >
            {t("common.create")}
          </RightButton>
        )}
      </BreadCrumbWrapper>
      <Table columns={columns} dataSource={clubs} onChange={onChange} pagination={false} />
      { createClubDrawerVisible && <CreateClubDrawer
        visible={createClubDrawerVisible}
        onClose={() => setCreateClubDrawerVisible(false)}
      /> }
      {selectedClub && (
        <EditClubDrawer
          visible={editClubDrawerVisible}
          onClose={() => {
            setSelectedClub(null);
            setEditClubDrawerVisible(false);
          }}
          club={selectedClub}
        />
      )}
      {selectedClub && (
        <DeleteClubModal
          visible={deleteClubModalVisible}
          onClose={() => {
            setSelectedClub(null);
            setDeleteClubModalVisible(false);
          }}
          club={selectedClub}
        />
      )}
    </Content>
  );
}
